import Markdoc from '@markdoc/markdoc';
import { locale } from '@riva-mono/common';

// Default fallback values if booking_delay is not available
const DEFAULT_HOURS_BEFORE_BOOKING_CANCEL_CONSULTATION = 24;
const DEFAULT_HOURS_BEFORE_BOOKING_CANCEL_CHECKUP = 48;

export const markdownToHtml = (doc: string) => {
	if (doc) {
		const _doc = doc.replace(/\t/g, '');
		const ast = Markdoc.parse(_doc);

		const transformedContent = Markdoc.transform(ast, {
			nodes: {
				link: {
					render: 'a',
					attributes: {
						href: { type: String, required: true },
						// Always add target="_blank" and rel="noopener noreferrer" for security
						target: { default: '_blank' },
						rel: { default: 'noopener noreferrer' }
					}
				}
			}
		});
		const html = Markdoc.renderers.html(transformedContent);
		//html = html.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&quot;', '"');

		return html;
	} else {
		return '';
	}
};

export const formatNationalId = (nationalId: string) => {
	let inputValue = nationalId;
	inputValue = inputValue.replace(/[^0-9]/g, '');
	// remove all non numeric characters
	let formattedValue = '';
	for (let i = 0; i < inputValue.length; i++) {
		if (i === 2 || i === 4 || i === 9) {
			formattedValue += '.';
		} else if (i === 6) {
			formattedValue += '-';
		}
		formattedValue += inputValue[i];
	}
	formattedValue = formattedValue.slice(0, 15);
	return formattedValue;
};

interface DateYearResult {
	valid: boolean;
	year?: number;
	age?: number;
}

export const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

export const getFormattedDate = (date: Date, options?: Intl.DateTimeFormatOptions | undefined) => {
	const _options = options || {
		dateStyle: 'full',
		timeStyle: 'short'
		// timeZone: 'Europe/Paris'
	};
	const storedLang = locale.get();
	const lang = storedLang === 'fr' ? 'fr-FR' : 'en-US';
	return new Intl.DateTimeFormat(lang, _options).format(date);
};

export function checkCardNumber(cardNumber: string): boolean {
	const firstTen = cardNumber.substring(0, 10);
	const lastTwo = cardNumber.substring(10, 12);
	return parseInt(firstTen) % 97 === parseInt(lastTwo);
}

export function extractDateYearFromNationalId(_number: string): DateYearResult | null {
	// let number: string = '82050108590';
	let number: string = _number.replaceAll('.', '').replaceAll('-', '');
	// Be sure to have something to test
	if (!number) {
		return null;
	}

	// Only take the first 11 characters
	number = number.substring(0, 11);

	// Test if we have well an 11-digit string
	if (!/\d{11}/.test(number)) {
		return null;
	}

	// Numbers we're going to check against
	let to_control: string = number.substring(0, 9);

	// Verification number
	const control: string = number.substring(9, 11);

	// Date parsing
	const date: string = number.substring(0, 6);
	const date_year: number = parseInt(date.substring(0, 2), 10);
	const date_month: number = parseInt(date.substring(2, 4), 10);
	const date_day: number = parseInt(date.substring(4, 6), 10);
	const today_year: number = parseInt(new Date().getFullYear().toString().substring(2, 4), 10);

	// Simple date check
	if (date_month < 1 || date_month > 12 || date_day < 1 || date_day > 31) {
		return null;
	}
	let millenials = false;
	// Add '2' before if date after 1st Jan 2000
	if (date_year < today_year) {
		millenials = true;
		to_control = '2' + to_control.toString();
	}

	// Modulo calculation
	const toControlAsNumber: number = parseInt(to_control, 10);
	let modulo: number = 97 - (toControlAsNumber % 97);
	modulo = Math.floor(modulo);
	modulo = modulo < 10 ? 0 + modulo : modulo;

	const year = parseInt(millenials ? '20' + date_year : '19' + date_year);

	// Final test
	return {
		valid: control === modulo.toString().padStart(2, '0'),
		year,
		age: new Date().getFullYear() - year
	};
}

export function validateUpdateBookingDate(
	bookingDate: Date | string,
	currentDate: Date,
	type: 'consultation' | 'checkup' = 'checkup',
	bookingDelay?: number
) {
	// Result object structure
	const result: {
		isValid: boolean;
		message: string | null;
		params: Record<string, any>;
	} = {
		isValid: false,
		message: null,
		params: {}
	};

	// Validate input parameters
	const bookingDateObj = new Date(bookingDate);
	const currentDateObj = new Date(currentDate);

	// Check if dates are valid
	if (isNaN(bookingDateObj.getTime())) {
		result.message = 'invalid_date';
		return result;
	}

	if (isNaN(currentDateObj.getTime())) {
		result.message = 'invalid_date';
		return result;
	}

	// Calculate time difference in hours between current date and booking date
	const hoursDifference = (bookingDateObj.getTime() - currentDateObj.getTime()) / (1000 * 60 * 60);

	// Use provided booking delay or fallback to default values
	const hours =
		bookingDelay ||
		(type === 'checkup'
			? DEFAULT_HOURS_BEFORE_BOOKING_CANCEL_CHECKUP
			: DEFAULT_HOURS_BEFORE_BOOKING_CANCEL_CONSULTATION);

	if (hoursDifference < hours) {
		result.message = 'invalid_advance_booking';
		result.params = { hours };
		return result;
	}

	// Check if booking is for a Monday
	const isBookingMonday = bookingDateObj.getDay() === 1; // 0 is Sunday, 1 is Monday

	if (isBookingMonday && type === 'checkup') {
		// Create a date for the previous Friday at 5pm
		const previousFridayAt5PM = new Date(bookingDateObj);
		// Go back to previous Friday (3 days back from Monday)
		previousFridayAt5PM.setDate(bookingDateObj.getDate() - 3);
		previousFridayAt5PM.setHours(17, 0, 0, 0); // Set to 5:00:00 PM

		// If current time is after Friday 5PM, booking for Monday is invalid
		if (currentDateObj > previousFridayAt5PM) {
			result.message = 'invalid_monday_booking';
			return result;
		}
	}

	// If we get here, the booking is valid
	result.isValid = true;
	result.message = 'valid_booking';
	return result;
}

/**
 * Fetch booking delay from consultation type
 * @param supabase - Supabase client
 * @param consultationTypeSlug - The consultation type slug
 * @returns The booking delay in hours, or null if not found
 */
export async function getBookingDelayFromConsultationType(
	supabase: any,
	consultationTypeSlug: string
): Promise<number | null> {
	try {
		const { data, error } = await supabase
			.from('consultation_type')
			.select('booking_delay')
			.eq('slug', consultationTypeSlug)
			.single();

		if (error) {
			console.warn('Error fetching booking delay:', error);
			return null;
		}

		return data?.booking_delay || null;
	} catch (error) {
		console.warn('Error fetching booking delay:', error);
		return null;
	}
}

// Example usage:
/*
// Valid date scenario
console.log(validateBooking('2025-03-24T10:00:00', '2025-03-21T16:00:00'));
// Expected: { isValid: true, message: "Booking is valid." }

// Invalid date string scenario
console.log(validateBooking('not-a-date', '2025-03-21T16:00:00'));
// Expected: { isValid: false, message: "Invalid booking date provided." }

// Another invalid scenario
console.log(validateBooking('2025-03-24T10:00:00', 'invalid-current-date'));
// Expected: { isValid: false, message: "Invalid current date provided." }
*/
